import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Supabase } from '../services/supabase';
import { Database } from '../services/database';
import { MessageService } from 'primeng/api';
import { User } from '../models/database.types';

export const onboardingResolver: ResolveFn<boolean> = async () => {
  const supabase = inject(Supabase);
  const db = inject(Database);
  const router = inject(Router);
  const toast = inject(MessageService);

  try {
    // Aktuelle Session laden
    const { data: sessionData } = await supabase.getClient().auth.getSession();

    if (!sessionData.session) {
      // Kein aktiver User → zurück zur Login
      router.navigate(['/auth/login']);
      return false;
    }

    const user = sessionData.session.user;

    // 1) Prüfen ob E-Mail bestätigt ist
    if (!user?.email_confirmed_at) {
      // Abgelaufen? -> neues Mail senden
      const tokenExpiry = user?.confirmation_sent_at
        ? new Date(user.confirmation_sent_at).getTime() + 1000 * 60 * 60 * 24 // Beispiel 24h
        : 0;

      if (Date.now() > tokenExpiry) {
        if (user.email) {
          await supabase.resendVerificationMail(user.email);
        }
        toast.add({
          severity: 'warn',
          summary: 'Verifizierung abgelaufen',
          detail: 'Wir haben dir ein neues Verifizierungs-Mail gesendet.'
        });
        router.navigate(['/auth/login']);
        return false;
      }

      // Link noch gültig → User bleibt im Onboarding
      return true;
    }

    // 2) Prüfen ob ein public.users Datensatz existiert
    let dbUser: User | null = null;
    try {
      dbUser = await firstValueFrom(db.getUserById(user.id));
    } catch {
      dbUser = null;
    }

    if (!dbUser) {
      // Fall A: Auth-User existiert, aber noch kein public.user → Onboarding starten
      return true;
    }

    if (!dbUser.company_id) {
      // Fall B: User vorhanden, aber keine Firma → Onboarding Step 2 fortsetzen
      return true;
    }

    // 3) Company laden und prüfen
    const company = await firstValueFrom(db.getCompanyById(dbUser.company_id));
    if (company) {
      // Fall C: Alles abgeschlossen → Dashboard
      router.navigate(['/dashboard']);
      return false;
    }

    // Fallback → normal Onboarding
    return true;
  } catch (error) {
    console.error('Fehler im OnboardingResolver:', error);
    toast.add({
      severity: 'error',
      summary: 'Fehler',
      detail: 'Beim Onboarding ist ein Fehler aufgetreten. Bitte erneut einloggen.'
    });
    router.navigate(['/auth/login']);
    return false;
  }
};
